import React, { ReactNode, useContext, useEffect } from 'react';
import { AxiosError } from 'axios';
import useLastSchoolyearSchool from '@teetsh/app/src/queries/schoolyears/useLastSchoolyearSchool';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { ErrorPage } from '@teetsh/app/src/components/ErrorPage';
import { schoolyearSchoolAtom } from '@teetsh/app/src/containers/App/context/atom';
import { useAtom } from 'jotai';
import { SchoolSchoolyearContext } from './SchoolSchoolyearContext';
import { useCurrentSchoolyearIdSchoolId } from '@teetsh/app/src/queries/schoolyears/atom';
import { Navigate } from 'react-router-dom';

function useSchoolSchoolyear() {
  const context = useContext(SchoolSchoolyearContext);
  if (context === undefined) {
    throw new Error(
      'useSchoolSchoolyear must be used within a SchoolSchoolyearProvider',
    );
  }
  return context;
}

interface Props {
  schoolyearId?: string;
  schoolId?: string;
  disabled?: boolean;
  children: ReactNode;
}

function SchoolSchoolyearProvider(props: Props) {
  const { schoolId, schoolyearId, disabled, ...rest } = props;
  const [, set] = useAtom(schoolyearSchoolAtom);
  const { data, isLoading, isError, error, isPaused } = useLastSchoolyearSchool(
    {
      schoolyearId,
      schoolId,
    },
  );

  const { reset } = useCurrentSchoolyearIdSchoolId();
  useEffect(() => {
    if (schoolyearId && schoolId) {
      set({
        schoolyearId,
        schoolId,
      });
    }
  }, [schoolyearId, schoolId]);

  const status = (error as AxiosError)?.response?.status;
  const is4xx = status ? status >= 400 && status < 500 : false;
  if (is4xx) {
    reset();
    if (schoolyearId && schoolId) {
      return <Navigate to="/" />;
    }
    // Si c'est getLastSchool qui est appelé et que ça retourne 404, c'est qu'il n'y a pas d'école, donc probablement encore en cours d'onboarding donc au redirige au début de l'onboarding

    if (status === 404) {
      return <Navigate to="/demarrage" />;
    }
  }

  if (disabled) {
    return (
      <SchoolSchoolyearContext.Provider
        value={{
          school: { id: schoolId } as any,
          schoolyear: { id: schoolyearId } as any,
        }}
        {...rest}
      />
    );
  }
  // Si pas de data est qu'on est en pause c'est qu'on est offline au démarrage
  if (isLoading || (!data && isPaused)) {
    return <SpinnerComponent />;
  }
  if (isError) {
    return <ErrorPage />;
  }
  return <SchoolSchoolyearContext.Provider value={data} {...rest} />;
}

export { SchoolSchoolyearProvider, useSchoolSchoolyear };
